import Guid from "_shared/guids/guid";
import Forum from "messaging/forums/forum";
import { User } from "users/user";
import AHDate from "_shared/dates/date";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import MessageSchema, { MessageJSON, MessageParams } from "messaging/_schemas/message-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { reportUnreadableRecord } from "_shared/_errors/client-error-reporter";

export interface DraftParams {
  id: Guid;
  created: AHDate;
  forum: Forum;
  sender: User;
  content?: string;
  subject?: string;
  attachments?: File[];
}

export default class Message {
  public static readonly EXAMPLE: Message = new Message({
    id: Guid.generate(),
    senderId: Guid.generate(),
    forum: Forum.EXAMPLE,
    content: "This is a test message.",
    subject: "Test Message",
    attachments: [MessageFileInfo.EXAMPLE],
    readReceipts: [],
    issues: [],
    created: new AHDate(),
    updated: new AHDate(),
    published: new AHDate(),
  });

  public static create(params: MessageParams): Message {
    const parsed = MessageSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Message.Create(): Invalid parameters.");
    }
    return new Message(parsed);
  }

  public static fromJSON(params: { json: unknown }): Message {
    const { json } = params;
    if (isNullOrUndefined(json)) throw new Error("Message.fromJSON(): JSON object is null or undefined.");
    const messageParams = MessageSchema.deserialize(json);
    return new Message(messageParams);
  }

  /**
   * Every message in `jsonArray` that parses. A row that does not is reported once and left out, so one
   * bad message cannot fail the list, or the read receipt batch, that carries it (#6592).
   */
  public static fromJSONArray(params: { jsonArray: readonly unknown[] }): Message[] {
    const messages: Message[] = [];
    for (const json of params.jsonArray) {
      try {
        messages.push(Message.fromJSON({ json }));
      } catch {
        reportUnreadableRecord({
          record: "Message",
          operation: "fromJSONArray",
          id: idOf(json),
          parentId: idOf(fieldOf(json, "forum")),
        });
      }
    }
    return messages;
  }

  public static draft(params: DraftParams): Message {
    const { id, created, forum, sender, content, subject, attachments } = params;

    if (!content && (!attachments || attachments.length === 0)) throw new EmptyMessageError();
    if (!sender.id) throw new Error("Must be logged in to draft a message");

    const draftAttachments =
      attachments?.map((attachment: File) => {
        return MessageFileInfo.fromFile(attachment);
      }) ?? [];

    let newMessage = new Message({
      ...params,
      id: id,
      created: created,
      forum: forum,
      senderId: sender.id,
      content: content,
      subject: subject,
      attachments: draftAttachments,
    });

    return newMessage;
  }

  public readonly id: Guid;
  public readonly senderId: Guid | null;
  public readonly forum: Forum;
  public readonly content?: string;
  public readonly subject?: string;
  public readonly proposalFieldPath?: string;
  public readonly attachments: MessageFileInfo[] = [];
  /**
   * How many attachments the message carries. The same as `attachments.length` for a live message, and
   * the only trace of them on a deleted one: a tombstone indicates attachments without presenting them,
   * so `attachments` is empty while this still reports what went with the message.
   */
  public readonly attachmentCount: number = 0;
  public readonly readReceipts: ReadReceipt[] = [];
  public readonly issues: string[] = [];
  public readonly created?: AHDate;
  public readonly updated?: AHDate;
  public readonly published?: AHDate;
  /**
   * When the author last took this message back from its audience; undefined once it is published
   * again, and undefined on a message that was never published. That distinction is the whole point of
   * it: clearing `published` alone made a deliberate retraction look exactly like a never-sent draft.
   */
  public readonly retracted?: AHDate;

  /**
   * A live message with no content and no attachments. The server refuses to create one, but a stored
   * message can still lose its attachment, and the reader is told so rather than shown an empty bubble
   * (#6592). A deleted or retracted message has no body by design, and is not one of these.
   */
  public get hasNoBody(): boolean {
    if (this.isDeleted || this.retracted) return false;
    return !this.content?.trim() && this.attachments.length === 0;
  }

  public get isDeleted(): boolean {
    return Boolean(this._deleted);
  }

  public get deleted(): AHDate | undefined {
    return this._deleted;
  }

  public get isMarkedRead(): boolean {
    return this._markedRead ?? false;
  }

  public get isMarkedUnread(): boolean {
    return this._markedUnread ?? false;
  }

  public get isMarkedForCreation(): boolean {
    return this._markedForCreation ?? false;
  }

  public get isMarkedForDeletion(): boolean {
    return this._markedForDeletion ?? false;
  }

  public get isMarkedForPublish(): boolean {
    return this._markedForPublish ?? false;
  }

  public get isMarkedForUnpublish(): boolean {
    return this._markedForUnpublish ?? false;
  }

  public constructor(params: MessageParams) {
    const validatedParams = MessageSchema.validate(params);
    this.id = validatedParams.id;
    this.senderId = validatedParams.senderId ?? null;
    this.forum = Forum.create({ ...validatedParams, ...validatedParams.forum });
    this.content = validatedParams.content;
    this.subject = validatedParams.subject;
    this.proposalFieldPath = validatedParams.proposalFieldPath ?? undefined;
    this.created = validatedParams.created;
    this.published = validatedParams.published;
    this.retracted = validatedParams.retracted;
    this.attachments =
      validatedParams.attachments?.map(
        (attachment) =>
          new MessageFileInfo({
            id: attachment.id,
            name: attachment.name,
            fileSize: attachment.fileSize,
            mimeType: attachment.mimeType,
            createdDate: attachment.createdDate,
            rawData: attachment.rawData,
            dataUri: attachment.dataUri,
            // How far the file's processing got, which the bubble draws under it (#6877).
            scanStatus: attachment.scanStatus,
            classificationStatus: attachment.classificationStatus,
            subjectMatters: attachment.subjectMatters,
            // Whether this reader may download it, which disables Download when not (#6993).
            contentAccess: attachment.contentAccess,
            restrictedSubjectMatters: attachment.restrictedSubjectMatters,
          }),
      ) ?? [];
    this.attachmentCount = validatedParams.attachmentCount ?? this.attachments.length;
    this.issues = validatedParams.issues ?? [];
    this.readReceipts = validatedParams.readReceipts ?? [];
    this.updated = validatedParams.updated;
    this._deleted = validatedParams.deleted;
  }

  public delete(): void {
    this._deleted = new AHDate();
    this._markedForDeletion = true;
  }

  public restore(): void {
    this._deleted = undefined;
    this._markedForDeletion = false;
  }

  public markForCreation(): void {
    this._markedForCreation = true;
  }

  public markRead(): void {
    this._markedRead = true;
    this._markedUnread = false;
  }

  public markUnread(): void {
    this._markedUnread = true;
    this._markedRead = false;
  }

  public isReadByUser(userId?: Guid): boolean {
    if (!userId) return false;
    return this.readReceipts.some((receipt) => receipt.userId.isEqualTo(userId));
  }

  /**
   * This message as it reads once `userId` has read it: the same message with their read receipt. The
   * message itself when they have one already.
   */
  public readBy(userId: Guid, seenAt: AHDate = new AHDate()): Message {
    if (this.isReadByUser(userId)) return this;
    return new Message({
      ...this,
      forum: this.forum,
      deleted: this._deleted,
      readReceipts: [...this.readReceipts, new ReadReceipt({ userId, seenAt })],
    });
  }

  public userIsSender(user: User): boolean {
    return this.senderId ? this.senderId?.isEqualTo(user.id) : false;
  }

  public publish(): void {
    this._markedForPublish = true;
    this._markedForUnpublish = false;
  }

  public unpublish(): void {
    this._markedForUnpublish = true;
    this._markedForPublish = false;
  }

  public addAttachments(attachments: MessageFileInfo[]): void {
    this.attachments.push(...attachments);
  }

  public toJSON(): MessageJSON {
    return MessageSchema.serialize(this);
  }

  public isEqualTo(other: Message): boolean {
    if (!other) return false;
    const idsEqual = Boolean((!this.id && !other.id) || (this.id && other.id && this.id.isEqualTo(other.id)));
    return idsEqual;
  }

  public toString(): string {
    return `Message { 
      id: ${this.id?.toString() ?? "undefined"},
      senderId: ${this.senderId?.toString() ?? "undefined"},
      forumId: ${this.forum.id?.toString() ?? "undefined"},
      subject: ${this.subject ?? "undefined"},
      content: ${this.content},
      createdDate: ${this.created?.toString() ?? "undefined"},
      updatedDate: ${this.updated?.toString() ?? "undefined"},
      publishedDate: ${this.published?.toString() ?? "undefined"},
      isDeleted: ${this.isDeleted},
      attachmentsCount: ${this.attachments.length},
      readReceiptsCount: ${this.readReceipts.length}
    }`;
  }

  private _deleted?: AHDate;
  private _markedRead?: boolean;
  private _markedUnread?: boolean;
  private _markedForCreation?: boolean;
  private _markedForDeletion?: boolean;
  private _markedForPublish?: boolean;
  private _markedForUnpublish?: boolean;
}

/** The `id` of a payload that may not be an object, for a report about a payload that did not parse. */
function idOf(json: unknown): string | undefined {
  const id = fieldOf(json, "id");
  return typeof id === "string" ? id : undefined;
}

/** A field of a payload that may not be an object. */
function fieldOf(json: unknown, field: string): unknown {
  if (typeof json !== "object" || json === null || !(field in json)) return undefined;
  return (json as Record<string, unknown>)[field];
}

export class EmptyMessageError extends Error {
  constructor() {
    super(`Message cannot be empty`);
  }
}

/**
 * Orders messages as a conversation reads, oldest first: by when each was written, then published, then
 * by id, the same total order the server pages in. A chat sorts by it rather than trusting the order a
 * page or a realtime event arrived in, which a server that does not know `newestFirst` gets backwards.
 */
export function inReadingOrder(a: Message, b: Message): number {
  const byDate = (a.created?.diff(b.created) ?? 0) || (a.published?.diff(b.published) ?? 0);
  if (byDate !== 0) return byDate;
  const left = a.id?.value ?? "";
  const right = b.id?.value ?? "";
  if (left === right) return 0;
  return left < right ? -1 : 1;
}
